import { FC } from 'react';
import { useInView } from 'react-intersection-observer';

import { FloatingTab, FloatingTabs } from '@dbt-labs/sourdough';

import { ResourceLinkList } from '@/components/ResourceLinkList';
import { DetailsSection } from '@/components/SectionWithCard';
import { useUrlProjectParams } from '@/hooks/params';
import { useRelationshipLinks } from '@/hooks/useRelationshipLinks';
import { ParsedUniqueId } from '@/typings/parseUniqueId';

interface ReferencesAndDependenciesSectionProps {
  uniqueId: ParsedUniqueId;
}

export const ReferencesAndDependenciesSection: FC<
  ReferencesAndDependenciesSectionProps
> = ({ uniqueId }) => {
  const { inView, ref } = useInView({ triggerOnce: true });

  const { links, parents, children, activeTab, setActiveTab, isFetching } =
    useRelationshipLinks({
      uniqueId,
      projectScope: useUrlProjectParams(),
      onlyReferencedByProjects: false,
      pause: !inView,
    });

  const noRelationships = !isFetching && !parents?.length && !children?.length;

  return (
    <DetailsSection heading="Relationships">
      <div className="space-y-6" ref={ref}>
        <div className="overflow-x-auto">
          {noRelationships && (
            <p className="italic text-fgDecorative">No relationships found.</p>
          )}
          {!noRelationships && (
            <FloatingTabs>
              {!!parents?.length && (
                <FloatingTab
                  text="Depends on"
                  id="parents"
                  isActive={activeTab === 'parents'}
                  onClick={() => setActiveTab('parents')}
                />
              )}
              {!!children?.length && (
                <FloatingTab
                  text="Referenced by"
                  id="children"
                  isActive={activeTab === 'children'}
                  onClick={() => setActiveTab('children')}
                />
              )}
            </FloatingTabs>
          )}
        </div>
        {!noRelationships && <ResourceLinkList links={links} />}
      </div>
    </DetailsSection>
  );
};
